import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Plus, Trash2, Loader2, Info, Eye, EyeOff, Clock, Check, Pencil, X } from 'lucide-react';
import { useToast } from '../../../components/ToastProvider';
import {
  useReviews,
  useCreateReview,
  useUpdateReview,
  useDeleteReview,
  useActiveProperty,
  type Review,
  type ReviewSource,
  type ReviewStatus,
} from '../../../hooks/useManagement';
import PropertySelector from '../../../components/PropertySelector';

const SOURCES: ReviewSource[] = ['direct', 'airbnb', 'booking', 'google', 'other'];

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500';

function Stars({ value, onChange }: { value: number; onChange?: (v: number) => void }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= value;
        const star = (
          <Star
            size={onChange ? 22 : 16}
            className={filled ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}
          />
        );
        return onChange ? (
          <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${n}`} className="p-0.5">
            {star}
          </button>
        ) : (
          <span key={n}>{star}</span>
        );
      })}
    </div>
  );
}

export default function ManageReviewsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { propertyId, properties } = useActiveProperty();
  const hasProperties = properties.length > 0;

  const [statusFilter, setStatusFilter] = useState<'all' | ReviewStatus>('all');
  const { data: reviews, isLoading } = useReviews(propertyId, statusFilter);
  const createReview = useCreateReview();
  const updateReview = useUpdateReview();
  const deleteReview = useDeleteReview();

  // ── Import / add form ──
  const emptyDraft = {
    author_name: '',
    author_country: '',
    rating: 5,
    title: '',
    body: '',
    source: 'airbnb' as ReviewSource,
    review_date: '',
    scope: 'property' as 'property' | 'all',
  };
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [formError, setFormError] = useState('');

  // ── Inline edit ──
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<Review>>({});

  const sourceLabel = (s: string): string =>
    t(`portal.manage.reviews.source.${s}`,
      s === 'direct' ? 'Directa' : s === 'airbnb' ? 'Airbnb' : s === 'booking' ? 'Booking' : s === 'google' ? 'Google' : 'Otra');

  const statusLabel = (s: string): string =>
    t(`portal.manage.reviews.status.${s}`,
      s === 'published' ? 'Publicada' : s === 'pending' ? 'Pendiente' : 'Oculta');

  const submitImport = async () => {
    setFormError('');
    if (!draft.author_name.trim()) { setFormError(t('portal.manage.reviews.errAuthor', 'Indica el nombre del autor.')); return; }
    try {
      await createReview.mutateAsync({
        author_name: draft.author_name.trim(),
        author_country: draft.author_country.trim() || null,
        rating: draft.rating,
        title: draft.title.trim() || null,
        body: draft.body.trim() || null,
        source: draft.source,
        review_date: draft.review_date || null,
        status: 'published',
        property_id: draft.scope === 'all' ? null : propertyId,
      });
      setDraft(emptyDraft);
      setShowForm(false);
      toast.success(t('portal.manage.reviews.created', 'Reseña añadida'));
    } catch {
      setFormError(t('portal.manage.reviews.errSave', 'No se pudo guardar. Inténtalo de nuevo.'));
    }
  };

  const startEdit = (r: Review) => {
    setEditingId(r.id);
    setEditDraft({
      author_name: r.author_name,
      author_country: r.author_country,
      rating: r.rating,
      title: r.title,
      body: r.body,
      source: r.source,
      review_date: r.review_date,
    });
  };
  const saveEdit = async (id: number) => {
    try {
      await updateReview.mutateAsync({ id, ...editDraft });
      setEditingId(null);
      toast.success(t('common.savedToast', 'Cambios guardados'));
    } catch {
      toast.error(t('common.saveError', 'No se ha podido guardar. Inténtalo de nuevo.'));
    }
  };
  const setStatus = (id: number, status: ReviewStatus) => updateReview.mutate({ id, status });

  const list = reviews ?? [];
  const pendingCount = list.filter((r) => r.status === 'pending').length;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Star className="text-amber-400" size={24} />
            {t('portal.manage.reviews.title', 'Reseñas')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t('portal.manage.reviews.subtitle', 'Importa tus reseñas de Airbnb/Booking y modera las de huéspedes directos.')}
          </p>
        </div>
        <PropertySelector allowAll />
      </div>

      {/* Helper note — import is manual */}
      <div className="flex gap-3 rounded-xl bg-teal-50 p-4 ring-1 ring-teal-100">
        <Info className="shrink-0 text-teal-600" size={18} />
        <p className="text-sm text-teal-800">
          {t('portal.manage.reviews.importNote', 'Pega aquí tus reseñas de Airbnb/Booking (no existe forma automática de importarlas).')}
        </p>
      </div>

      {!hasProperties && (
        <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800 ring-1 ring-amber-100">
          {t('portal.manage.reviews.noProperties', 'Crea primero un alojamiento para gestionar sus reseñas.')}
        </p>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex flex-wrap items-center gap-1 rounded-xl bg-slate-100 p-1">
          {(['all', 'pending', 'published', 'hidden'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                statusFilter === s ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {s === 'all' ? t('portal.manage.reviews.filterAll', 'Todas') : statusLabel(s)}
              {s === 'pending' && pendingCount > 0 && (
                <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          disabled={!hasProperties}
          className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300"
        >
          <Plus size={16} /> {t('portal.manage.reviews.import', 'Importar reseña')}
        </button>
      </div>

      {/* Import form */}
      {showForm && hasProperties && (
        <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.author', 'Autor')}</span>
              <input className={inputCls} value={draft.author_name} onChange={(e) => setDraft({ ...draft, author_name: e.target.value })} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.country', 'País (opcional)')}</span>
              <input className={inputCls} value={draft.author_country} onChange={(e) => setDraft({ ...draft, author_country: e.target.value })} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.source.label', 'Fuente')}</span>
              <select className={inputCls} value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value as ReviewSource })}>
                {SOURCES.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.date', 'Fecha (opcional)')}</span>
              <input type="date" className={inputCls} value={draft.review_date} onChange={(e) => setDraft({ ...draft, review_date: e.target.value })} />
            </label>
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.rating', 'Valoración')}</span>
            <Stars value={draft.rating} onChange={(v) => setDraft({ ...draft, rating: v })} />
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.reviewTitle', 'Título (opcional)')}</span>
            <input className={inputCls} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.text', 'Texto')}</span>
            <textarea className={inputCls} rows={3} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">{t('portal.manage.reviews.scope', 'Aplica a')}</span>
            <select className={inputCls} value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value as 'property' | 'all' })}>
              <option value="property">{t('portal.manage.reviews.scopeProperty', 'Este alojamiento')}</option>
              <option value="all">{t('portal.manage.reviews.scopeAll', 'Todo el negocio')}</option>
            </select>
          </label>
          {formError && <p className="text-sm text-rose-600">{formError}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setShowForm(false); setDraft(emptyDraft); setFormError(''); }} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">
              {t('common.cancel', 'Cancelar')}
            </button>
            <button type="button" onClick={() => void submitImport()} disabled={createReview.isPending} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300">
              {createReview.isPending && <Loader2 size={16} className="animate-spin" />} {t('common.save', 'Guardar')}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-teal-500" size={28} /></div>
      ) : list.length === 0 ? (
        <p className="rounded-xl bg-slate-50 p-8 text-center text-sm text-slate-500 ring-1 ring-slate-100">
          {t('portal.manage.reviews.empty', 'Aún no hay reseñas.')}
        </p>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (
            <div key={r.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-100">
              {editingId === r.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <input className={inputCls} value={editDraft.author_name ?? ''} onChange={(e) => setEditDraft({ ...editDraft, author_name: e.target.value })} placeholder={t('portal.manage.reviews.author', 'Autor')} />
                    <input className={inputCls} value={editDraft.author_country ?? ''} onChange={(e) => setEditDraft({ ...editDraft, author_country: e.target.value })} placeholder={t('portal.manage.reviews.country', 'País')} />
                  </div>
                  <Stars value={editDraft.rating ?? 5} onChange={(v) => setEditDraft({ ...editDraft, rating: v })} />
                  <input className={inputCls} value={editDraft.title ?? ''} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} placeholder={t('portal.manage.reviews.reviewTitle', 'Título')} />
                  <textarea className={inputCls} rows={3} value={editDraft.body ?? ''} onChange={(e) => setEditDraft({ ...editDraft, body: e.target.value })} placeholder={t('portal.manage.reviews.text', 'Texto')} />
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditingId(null)} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100">
                      <X size={15} /> {t('common.cancel', 'Cancelar')}
                    </button>
                    <button type="button" onClick={() => void saveEdit(r.id)} disabled={updateReview.isPending} className="inline-flex items-center gap-1 rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:bg-slate-300">
                      <Check size={15} /> {t('common.save', 'Guardar')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{r.author_name}</span>
                        {r.author_country && <span className="text-xs text-slate-400">{r.author_country}</span>}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <Stars value={r.rating} />
                        {r.review_date && <span className="text-xs text-slate-400">{r.review_date}</span>}
                        {r.property_name && <span className="text-xs text-teal-600 truncate max-w-[14rem]">· {r.property_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{sourceLabel(r.source)}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        r.status === 'published' ? 'bg-emerald-100 text-emerald-700'
                          : r.status === 'pending' ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-200 text-slate-600'
                      }`}>{statusLabel(r.status)}</span>
                    </div>
                  </div>
                  {r.title && <p className="mt-3 font-medium text-slate-800">{r.title}</p>}
                  {r.body && <p className="mt-1 text-sm leading-relaxed text-slate-600 whitespace-pre-line">{r.body}</p>}

                  <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                    {r.status !== 'published' && (
                      <button type="button" onClick={() => setStatus(r.id, 'published')} className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                        <Eye size={14} /> {t('portal.manage.reviews.publish', 'Publicar')}
                      </button>
                    )}
                    {r.status !== 'hidden' && (
                      <button type="button" onClick={() => setStatus(r.id, 'hidden')} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200">
                        <EyeOff size={14} /> {t('portal.manage.reviews.hide', 'Ocultar')}
                      </button>
                    )}
                    {r.status !== 'pending' && (
                      <button type="button" onClick={() => setStatus(r.id, 'pending')} className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100">
                        <Clock size={14} /> {t('portal.manage.reviews.markPending', 'Pendiente')}
                      </button>
                    )}
                    <button type="button" onClick={() => startEdit(r)} className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">
                      <Pencil size={14} /> {t('common.edit', 'Editar')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (confirm(t('portal.manage.reviews.confirmDelete', '¿Eliminar esta reseña?'))) deleteReview.mutate(r.id); }}
                      className="ml-auto inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 size={14} /> {t('common.delete', 'Eliminar')}
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
